import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const LOADING_ANIMATION_PREFERENCE_STORAGE_KEY = 'resqLoadingAnimationPreference';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

export type LoadingAnimationPreference = 'system' | 'animated' | 'reduced';

interface LoadingAnimationPreferenceContextValue {
    loadingAnimationPreference: LoadingAnimationPreference;
    prefersReducedMotion: boolean;
    reduceLoadingMotion: boolean;
    setLoadingAnimationPreference: (preference: LoadingAnimationPreference) => void;
}

const LoadingAnimationPreferenceContext =
    createContext<LoadingAnimationPreferenceContextValue | null>(null);

const isLoadingAnimationPreference = (value: string | null): value is LoadingAnimationPreference =>
    value === 'system' || value === 'animated' || value === 'reduced';

const getStoredPreference = (): LoadingAnimationPreference => {
    if (typeof window === 'undefined') {
        return 'system';
    }

    try {
        const stored = window.localStorage.getItem(LOADING_ANIMATION_PREFERENCE_STORAGE_KEY);
        return isLoadingAnimationPreference(stored) ? stored : 'system';
    } catch (error) {
        console.warn('Failed to read loading animation preference from localStorage:', error);
        return 'system';
    }
};

const getPrefersReducedMotion = () => {
    if (typeof window === 'undefined' || !window.matchMedia) {
        return false;
    }

    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
};

export const LoadingAnimationPreferenceProvider = ({ children }: { children: React.ReactNode }) => {
    const [loadingAnimationPreference, setLoadingAnimationPreferenceState] =
        useState<LoadingAnimationPreference>(getStoredPreference);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(getPrefersReducedMotion);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return undefined;
        }

        const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);
        const handleChange = (event: MediaQueryListEvent) => {
            setPrefersReducedMotion(event.matches);
        };

        setPrefersReducedMotion(mediaQuery.matches);
        mediaQuery.addEventListener('change', handleChange);

        return () => {
            mediaQuery.removeEventListener('change', handleChange);
        };
    }, []);

    const setLoadingAnimationPreference = useCallback((preference: LoadingAnimationPreference) => {
        setLoadingAnimationPreferenceState(preference);
        try {
            window.localStorage.setItem(LOADING_ANIMATION_PREFERENCE_STORAGE_KEY, preference);
        } catch (error) {
            console.warn('Failed to persist loading animation preference to localStorage:', error);
        }
    }, []);

    const reduceLoadingMotion =
        loadingAnimationPreference === 'reduced' ||
        (loadingAnimationPreference === 'system' && prefersReducedMotion);

    const value = useMemo(
        () => ({
            loadingAnimationPreference,
            prefersReducedMotion,
            reduceLoadingMotion,
            setLoadingAnimationPreference,
        }),
        [
            loadingAnimationPreference,
            prefersReducedMotion,
            reduceLoadingMotion,
            setLoadingAnimationPreference,
        ],
    );

    return (
        <LoadingAnimationPreferenceContext.Provider value={value}>
            {children}
        </LoadingAnimationPreferenceContext.Provider>
    );
};

export const useLoadingAnimationPreference = () => {
    const context = useContext(LoadingAnimationPreferenceContext);
    if (!context) {
        throw new Error(
            'useLoadingAnimationPreference must be used within LoadingAnimationPreferenceProvider',
        );
    }

    return context;
};
